import { useState } from 'react';

interface VehicleGridProps {
  selectedCategory: string;
  priceRange: number[];
  selectedFeatures: string[];
}

export default function VehicleGrid({ selectedCategory, priceRange, selectedFeatures }: VehicleGridProps) {
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const vehicles = [
    {
      id: 1,
      name: 'Toyota Corolla',
      category: 'economy',
      price: 35,
      image: 'https://public.readdy.ai/ai/img_res/b100aa7b8d15c02575ca990ff9e673ef.jpg',
      features: ['Automatic Transmission', 'Air Conditioning', 'Bluetooth'],
      specs: { seats: 5, transmission: 'Automatic', fuel: 'Gasoline', mpg: '32 MPG' }
    },
    {
      id: 2,
      name: 'Honda Civic',
      category: 'economy',
      price: 38,
      image: 'https://readdy.ai/api/search-image?query=Silver%20Honda%20Civic%20sedan%20in%20professional%20car%20rental%20setting%2C%20clean%20modern%20background%2C%20studio%20lighting%2C%20three-quarter%20front%20view%20showcasing%20contemporary%20design%20and%20reliability&width=400&height=300&seq=car2&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'Backup Camera'],
      specs: { seats: 5, transmission: 'Automatic', fuel: 'Gasoline', mpg: '30 MPG' }
    },
    {
      id: 3,
      name: 'BMW 3 Series',
      category: 'luxury',
      price: 85,
      image: 'https://readdy.ai/api/search-image?query=Elegant%20black%20BMW%203%20Series%20luxury%20sedan%20in%20premium%20showroom%20environment%2C%20sophisticated%20lighting%2C%20clean%20minimalist%20background%2C%20front%20three-quarter%20view%20highlighting%20luxury%20features%20and%20premium%20design&width=400&height=300&seq=car3&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'Heated Seats', 'Sunroof'],
      specs: { seats: 5, transmission: 'Automatic', fuel: 'Gasoline', mpg: '26 MPG' }
    },
    {
      id: 4,
      name: 'Toyota RAV4',
      category: 'suv',
      price: 65,
      image: 'https://readdy.ai/api/search-image?query=Modern%20blue%20Toyota%20RAV4%20SUV%20in%20professional%20automotive%20setting%2C%20clean%20white%20background%2C%20side%20view%20showcasing%20spacious%20design%20and%20versatility%2C%20bright%20studio%20lighting&width=400&height=300&seq=car4&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'All-Wheel Drive', 'Backup Camera'],
      specs: { seats: 7, transmission: 'Automatic', fuel: 'Gasoline', mpg: '28 MPG' }
    },
    {
      id: 5,
      name: 'Mercedes-Benz C-Class',
      category: 'luxury',
      price: 95,
      image: 'https://readdy.ai/api/search-image?query=Pristine%20white%20Mercedes-Benz%20C-Class%20luxury%20sedan%20in%20upscale%20showroom%2C%20premium%20lighting%2C%20sophisticated%20background%2C%20front%20angle%20view%20emphasizing%20luxury%20and%20elegance&width=400&height=300&seq=car5&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'Heated Seats', 'Sunroof', 'Bluetooth'],
      specs: { seats: 5, transmission: 'Automatic', fuel: 'Gasoline', mpg: '25 MPG' }
    },
    {
      id: 6,
      name: 'Ford Explorer',
      category: 'suv',
      price: 72,
      image: 'https://readdy.ai/api/search-image?query=Robust%20gray%20Ford%20Explorer%20SUV%20in%20modern%20car%20rental%20facility%2C%20clean%20professional%20background%2C%20three-quarter%20view%20showing%20spacious%20family-friendly%20design%20and%20capability&width=400&height=300&seq=car6&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'All-Wheel Drive', 'Heated Seats'],
      specs: { seats: 7, transmission: 'Automatic', fuel: 'Gasoline', mpg: '24 MPG' }
    },
    {
      id: 7,
      name: 'Nissan Altima',
      category: 'sedan',
      price: 42,
      image: 'https://readdy.ai/api/search-image?query=Sleek%20red%20Nissan%20Altima%20sedan%20in%20contemporary%20showroom%20setting%2C%20professional%20automotive%20photography%2C%20clean%20background%2C%20side%20profile%20highlighting%20modern%20sedan%20design&width=400&height=300&seq=car7&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'Bluetooth', 'Backup Camera'],
      specs: { seats: 5, transmission: 'Automatic', fuel: 'Gasoline', mpg: '29 MPG' }
    },
    {
      id: 8,
      name: 'Ford Transit Van',
      category: 'van',
      price: 89,
      image: 'https://readdy.ai/api/search-image?query=Professional%20white%20Ford%20Transit%20van%20in%20commercial%20vehicle%20setting%2C%20clean%20industrial%20background%2C%20side%20view%20showcasing%20cargo%20capacity%20and%20utility%20design%20for%20business%20use&width=400&height=300&seq=car8&orientation=landscape',
      features: ['Automatic Transmission', 'Air Conditioning', 'GPS Navigation', 'Backup Camera'],
      specs: { seats: 12, transmission: 'Automatic', fuel: 'Gasoline', mpg: '19 MPG' }
    }
  ];

  const filteredVehicles = vehicles.filter(vehicle => {
    const categoryMatch = selectedCategory === 'all' || vehicle.category === selectedCategory;
    const priceMatch = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];
    const featureMatch = selectedFeatures.length === 0 || 
      selectedFeatures.every(feature => vehicle.features.includes(feature));
    
    return categoryMatch && priceMatch && featureMatch;
  });

  const handleBookNow = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setSubmitSuccess(false);
  };

  const closeModal = () => {
    setSelectedVehicle(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4bsd3fhkbdsb76v7aq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          closeModal();
        }, 2000);
      }
    } catch (error) {
      console.error('Booking submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-2xl font-bold text-gray-800">
            Available Vehicles ({filteredVehicles.length})
          </h3>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Sort by:</span>
            <select className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8">
              <option>Price: Low to High</option>
              <option>Price: High to Low</option>
              <option>Most Popular</option>
              <option>Newest First</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredVehicles.map((vehicle) => (
            <div key={vehicle.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative">
                <img
                  src={vehicle.image}
                  alt={vehicle.name}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  ${vehicle.price}/day
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-800 mb-2">{vehicle.name}</h4>
                
                <div className="grid grid-cols-2 gap-2 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    {vehicle.specs.seats} seats
                  </div>
                  <div className="flex items-center">
                    <i className="ri-settings-line mr-2"></i>
                    {vehicle.specs.transmission}
                  </div>
                  <div className="flex items-center">
                    <i className="ri-gas-station-line mr-2"></i>
                    {vehicle.specs.fuel}
                  </div>
                  <div className="flex items-center">
                    <i className="ri-speed-line mr-2"></i>
                    {vehicle.specs.mpg}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {vehicle.features.slice(0, 3).map((feature, index) => (
                      <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {feature}
                      </span>
                    ))}
                    {vehicle.features.length > 3 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        +{vehicle.features.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => handleBookNow(vehicle)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredVehicles.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-car-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-2xl font-bold text-gray-600 mb-2">No vehicles found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more options</p>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Book {selectedVehicle.name}</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitSuccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">Booking Submitted!</h4>
                  <p className="text-gray-600">We'll contact you shortly to confirm your reservation.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <img
                      src={selectedVehicle.image}
                      alt={selectedVehicle.name}
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-4">
                        ${selectedVehicle.price}/day
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center">
                          <i className="ri-user-line mr-2"></i>
                          {selectedVehicle.specs.seats} seats
                        </div>
                        <div className="flex items-center">
                          <i className="ri-settings-line mr-2"></i>
                          {selectedVehicle.specs.transmission}
                        </div>
                        <div className="flex items-center">
                          <i className="ri-gas-station-line mr-2"></i>
                          {selectedVehicle.specs.fuel}
                        </div>
                        <div className="flex items-center">
                          <i className="ri-speed-line mr-2"></i>
                          {selectedVehicle.specs.mpg}
                        </div>
                      </div>
                    </div>
                  </div>

                  <form id="car_rental_booking" data-readdy-form onSubmit={handleSubmit}>
                    <input type="hidden" name="vehicle" value={selectedVehicle.name} />
                    <input type="hidden" name="daily_rate" value={`$${selectedVehicle.price}`} />

                    <div className="space-y-4 mb-6">
                      <h4 className="text-lg font-semibold text-gray-800">Rental Details</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pickup Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="pickup_date"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Return Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="return_date"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pickup Location <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="pickup_location"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                          >
                            <option value="">Select location</option>
                            <option value="East Brunswick Office - 28 Hollis Rd">East Brunswick Office - 28 Hollis Rd</option>
                            <option value="Newark Airport">Newark Airport</option>
                            <option value="JFK Airport">JFK Airport</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Return Location <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="return_location"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                          >
                            <option value="">Select location</option>
                            <option value="East Brunswick Office - 28 Hollis Rd">East Brunswick Office - 28 Hollis Rd</option>
                            <option value="Newark Airport">Newark Airport</option>
                            <option value="JFK Airport">JFK Airport</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 mb-6">
                      <h4 className="text-lg font-semibold text-gray-800">Your Information</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Driver's License Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="license_number"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Special Requests or Notes
                        </label>
                        <textarea
                          name="special_requests"
                          rows={3}
                          maxLength={500}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          placeholder="Any additional requirements or preferences..."
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:bg-blue-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
